import React from 'react';
import {
  Card,
  CardHeader,
  CardPreview,
  Title2,
  Body1,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import {
  // Finance & Accounting
  ReceiptMoney24Regular,
  MoneyHand24Regular,
  DocumentText24Regular,
  DataTrending24Regular,
  BookOpen24Regular,
  BuildingFactory24Regular,
  // Warehouse & Inventory
  BoxMultiple24Regular,
  Box24Regular,
  ClipboardTextEdit24Regular,
  // Transportation
  VehicleTruckProfile24Regular,
  CalendarClock24Regular,
  // Quality & Safety
  ShieldCheckmark24Regular,
  Warning24Regular,
  // Planning & Executive
  CalendarAgenda24Regular,
  PersonBoard24Regular,
  // Fallback
  Chat24Regular,
} from '@fluentui/react-icons';
import { AreaConfig } from '../types';

// Map icon names from JSON to actual icon components
const iconMap: Record<string, React.ReactElement> = {
  // Finance & Accounting
  ReceiptMoney: <ReceiptMoney24Regular />,
  MoneyHand: <MoneyHand24Regular />,
  DocumentText: <DocumentText24Regular />,
  DataTrending: <DataTrending24Regular />,
  BookOpen: <BookOpen24Regular />,
  BuildingFactory: <BuildingFactory24Regular />,
  // Warehouse & Inventory
  BoxMultiple: <BoxMultiple24Regular />,
  Box: <Box24Regular />,
  ClipboardTextEdit: <ClipboardTextEdit24Regular />,
  // Transportation
  VehicleTruckProfile: <VehicleTruckProfile24Regular />,
  CalendarClock: <CalendarClock24Regular />,
  // Quality & Safety
  ShieldCheckmark: <ShieldCheckmark24Regular />,
  Warning: <Warning24Regular />,
  // Planning & Executive
  CalendarAgenda: <CalendarAgenda24Regular />,
  PersonBoard: <PersonBoard24Regular />,
  // Fallback
  Chat: <Chat24Regular />,
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXXL}`,
    width: '100%',
    boxSizing: 'border-box',
  },
  title: {
    marginBottom: tokens.spacingVerticalM,
    textAlign: 'center',
    fontSize: '32px',
    fontWeight: '600',
    color: '#1a1a2e',
  },
  subtitle: {
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalXXL,
    textAlign: 'center',
    fontSize: '16px',
    maxWidth: '600px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalXL,
    width: '100%',
    maxWidth: '1400px',
    transition: 'opacity 0.15s ease',
  },
  gridLoading: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  card: {
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    borderRadius: '16px',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: '0 2px 8px rgba(10, 27, 69, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
    overflow: 'hidden',
    ':hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 12px 32px rgba(0, 120, 212, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(0, 120, 212, 0.3)',
    },
  },
  cardPreview: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    padding: '16px',
    fontSize: '32px',
  },
  cardContent: {
    padding: `0 ${tokens.spacingHorizontalM} ${tokens.spacingHorizontalM}`,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    '& svg': {
      width: '22px',
      height: '22px',
    },
  },
});

interface AreaSelectorProps {
  areas: AreaConfig[];
  onSelectArea: (area: AreaConfig) => void;
  userName?: string;
  isLoading?: boolean;
}

export const AreaSelector: React.FC<AreaSelectorProps> = ({
  areas,
  onSelectArea,
  userName,
  isLoading,
}) => {
  const styles = useStyles();

  const getIcon = (iconName: string, color?: string) => {
    const icon = iconMap[iconName] || <Chat24Regular />;
    return (
      <div 
        className={styles.iconWrapper}
        style={{ 
          backgroundColor: color ? `${color}20` : tokens.colorNeutralBackground3,
          color: color || tokens.colorBrandForeground1,
        }}
      >
        {icon}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <Title2 className={styles.title}>
        {userName ? `Hello, ${userName}!` : 'Welcome!'}
      </Title2>
      <Body1 className={styles.subtitle}>
        Explore prompts tailored to your role or business process. Browse by your role, pick the prompts that fit your needs, and use them to guide interactions with existing agents or to define requirements for future ones.
      </Body1>
      <div className={mergeClasses(styles.grid, isLoading && styles.gridLoading)}>
        {areas.map((area) => (
          <Card
            key={area.id}
            className={styles.card}
            onClick={() => onSelectArea(area)}
          >
            <CardPreview className={styles.cardPreview}>
              {getIcon(area.icon, area.color)}
            </CardPreview>
            <CardHeader
              header={<Title2>{area.name}</Title2>}
              description={<Body1>{area.description}</Body1>}
            />
          </Card>
        ))}
      </div>
    </div>
  );
};
